import { ReactNode } from "react";
import { ConflictTooltip } from "src/components/molecules/ConflictTooltip";
import { ConfiguratorContext_OptionFragment } from "src/fragments/circularFragments.generated";
import { Checkbox, DebugText, IconTooltip, LoadingBone, Radio, TooltipV2 } from "~components";
import { Css } from "~generated/css";
import { Maybe } from "~generated/graphqlTypes";
import { formatDelta, formatDeltaPriceDisplay, useTestIds } from "~utils";
import { RemovedOption } from "../../calcSelectedOptionIds";

// todo sc-66337 handle option images
// enum OptionImageType {
//   "Swatch",
//   "Display",
//   "Moodboard",
// }

// Places border between all children except last
export const OptionListContainer = ({ children }: { children: ReactNode }) => (
  <div
    css={
      Css.df.fdc.addIn(
        "> div:not(:last-child)",
        Css.add("borderBottom", "1px solid rgba(53, 53, 53, 0.16)")
          .add("borderBottomLeftRadius", 0)
          .add("borderBottomRightRadius", 0).$,
      ).$
    }
  >
    {children}
  </div>
);

type ChildOptionListItemProps = {
  option: ConfiguratorContext_OptionFragment;
  selected: boolean;
  groupName?: string;
  onClick: () => void;
};

type OptionListItemProps = {
  title: string;
  description: Maybe<string>;
  // 0 renders "Included"
  priceInCents: number;
  selected: boolean;
  onClick?: () => void;
  child?: boolean;
  conflicts?: RemovedOption[];
  optionId: string; // unique id for tooltip anchor
  showTBD?: boolean;
  // TODO: This will probably need some kind of grouping update if/when we create radioGroups
  // Uses radio button instead of checkbox
  isRadio?: boolean;
  groups?: { groupName: string; priceInCents: number; selected: boolean }[];
  // Render swatches
  children?: ChildOptionListItemProps[];
  autoSaving: boolean;
  disabled?: boolean;
};

export const OptionListItem = (props: OptionListItemProps) => {
  const {
    title,
    description,
    priceInCents,
    selected,
    onClick,
    conflicts,
    child = false,
    showTBD = false,
    isRadio = false,
    autoSaving,
    disabled = false,
  } = props;
  const tid = useTestIds(props, "option-list-item");

  return (
    <div
      css={{
        ...Css.df.fdc.w100.jcsb.py2.if(!props.groups).gap2.$,
        // If disabled, we want to show the normal state but not allow interaction
        ...Css.if(disabled).cursorNotAllowed.if(disabled && !selected).o25.$,
      }}
      {...tid}
    >
      {/* ListItem Description - Price (price may not show in some scenarios based on groupings, totals) */}
      <div css={Css.df.aifs.jcsb.gap2.$}>
        {/* 56% is sweetspot for line break */}
        <div css={Css.dif.w("56%").asc.$}>
          <span css={{ ...Css.t16.$, ...Css.if(child).pl1.fw300.if(child && !selected).gray700.$ }}>
            <span {...tid.title}>
              <DebugText>{props.optionId}&nbsp;</DebugText>
              {title}
            </span>
            {description && !conflicts && <IconTooltip message={description} type="info" {...tid.infoTooltip} />}
            {!disabled && conflicts?.nonEmpty && (
              <ConflictTooltip conflicts={conflicts} optionName={title} isAlreadySelected={selected} />
            )}
          </span>
        </div>
        <div css={Css.df.aic.gap1.$}>
          <span css={{ ...Css.t14.gray700.wsnw.$, ...(selected && Css.gray800.fw400.$) }}>
            {autoSaving ? <LoadingBone /> : formatDeltaPriceDisplay(priceInCents, showTBD)}
          </span>
          {isRadio ? (
            <Radio selected={selected} onClick={onClick} disabled={disabled} {...tid.radio} />
          ) : (
            <Checkbox selected={selected} onClick={onClick} disabled={disabled} {...tid.checkbox} />
          )}
        </div>
      </div>
      {/* Selectable swatches */}
      {props.children && !props.groups && (
        <div css={Css.df.gap1.$}>
          {props.children.map(({ option, selected, onClick }) => {
            // todo sc-66337 handle option images
            // const image = option?.images.find((i) => i.type.code === OptionImageType.Swatch);
            // const optionImage = image?.imageUrl || "https://via.placeholder.com/40x40";
            const optionImage = "https://via.placeholder.com/40x40";
            const anchorId = option.id.replace(":", "");
            return (
              <div
                key={option.id}
                id={anchorId}
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
                css={Css.cursorPointer.wPx(40).hPx(40).br100.ba.bw2.bcGray700.if(selected).bcGray800.bw2.$}
                {...tid.option}
              >
                <img src={optionImage} css={Css.br100.w100.h100.if(selected).ba.bw2.bcWhite.$} alt={option.name} />
                <TooltipV2 positionStrategy="fixed" anchorSelect={`#${anchorId}`}>
                  <SwatchTipContent imgSrc={optionImage} name={option.name} />
                </TooltipV2>
              </div>
            );
          })}
        </div>
      )}
      {/* {for n groups} */}
      {/* (indented) Child Group - Price */}
      {/* (indented) Selectable swatches */}
      {props.children &&
        props.groups &&
        props.groups.map(({ groupName, priceInCents, selected: groupSelected }, i) => {
          const children = props.children!.filter((child) => child.groupName === groupName);
          return (
            <div key={groupName} css={Css.df.gap2.fdc.$} {...tid.group}>
              <div css={Css.df.aic.jcsb.gap2.pl1.pt2.$}>
                {/* 56% is sweetspot for line break */}
                <div css={Css.dif.w("56%").$}>
                  <div css={Css.t14.fw300.if(!groupSelected).gray800.$}>{groupName}</div>
                </div>
                <div css={Css.df.gap("12px").$}>
                  <span
                    css={{
                      ...Css.t14.gray700.wsnw.$,
                      ...(groupSelected && Css.gray800.fw500.$),
                    }}
                  >
                    {priceInCents ? formatDelta(priceInCents) : groupSelected ? "Included" : "-"}
                  </span>
                </div>
              </div>
              {/* Flex wrap here is specifically for image swatches to not stretch */}
              <div css={Css.df.gap1.pl1.fww.$}>
                {children.map(({ option, selected, onClick }) => {
                  // const image = option.images.find((i) => i.type.code === OptionImageType.Swatch);
                  // const optionImage = image?.imageUrl || "https://via.placeholder.com/40x40";
                  const optionImage = "https://via.placeholder.com/40x40";
                  const anchorId = option.id.replace(":", "");
                  return (
                    <div
                      key={option.id}
                      id={anchorId}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                      }}
                      css={Css.cursorPointer.wPx(40).oh.hPx(40).br100.ba.bw2.bcGray700.if(selected).bcGray800.bw2.$}
                      {...tid.option}
                    >
                      <img
                        src={optionImage}
                        css={Css.br100.w100.h100.if(selected).ba.bw2.bcWhite.$}
                        alt={option.name}
                      />
                      <TooltipV2 positionStrategy="fixed" anchorSelect={`#${anchorId}`}>
                        <SwatchTipContent imgSrc={optionImage} name={option.name} />
                      </TooltipV2>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
    </div>
  );
};

function SwatchTipContent({ imgSrc, name }: { imgSrc: string; name: string }) {
  return (
    <div css={Css.df.fdc.gap(1.5).aic.$}>
      <span css={Css.fw500.gray800.$}>{name}</span>
      <img css={Css.bcGray100.ba.hPx(155).wPx(152).objectCover.$} src={imgSrc} alt={name} />
    </div>
  );
}

export function SkeletonOptionListItem() {
  return (
    <div
      css={{
        ...Css.df.fdc.w100.jcsb.py2.gap2.$,
      }}
    >
      {/* ListItem Description - Price (price may not show in some scenarios based on groupings, totals) */}
      <div css={Css.df.aifs.jcsb.gap2.$}>
        {/* 56% is sweetspot for line break */}
        <div css={Css.dif.w("56%").asc.$}>
          <span css={{ ...Css.t16.$ }}>
            <LoadingBone width="85px" />
          </span>
        </div>
        <div css={Css.df.aic.gap1.$}>
          <span css={{ ...Css.t14.gray700.wsnw.$ }}>
            <LoadingBone />
          </span>
          <Radio selected={false} disabled />
        </div>
      </div>
    </div>
  );
}
